import { useNavigate } from '@tanstack/react-router';
import { Card } from 'components/Card';
import Loader from 'components/Loader/Loader.component';
import smallChevronRightSvg from 'images/icons/chevron-small-black-right.svg';
import { useEffect, useRef, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { requestDataIfNecessary } from 'services/api/api.actions';
import {
  selectBonusSpendability,
  selectBonusSpendabilityLoadState,
  selectGetBonusSpendabilityApiConfig,
} from 'services/bonus/bonus.selectors';
import { useI18n } from 'services/i18n/i18n.hooks';
import { InternalLink } from 'style/anchors.style';
import { Flex } from 'style/flexbox.style';
import styled from 'styled-components';
import { useIsAtLeastDesktopBreakpoint } from 'utils/hooks/media-queries';
import {
  ShowSpendibilityDropdown,
  SpecsParagraph,
  SpecsTitle,
  TableRowSpecs,
  TableSpecsDropdown,
} from '../BonusCardSpecs/style/BonusCardSpecs.style';
import { useBonusData } from './BonusAmountAvailable.hooks';
import {
  DropdownBoldTableCell,
  DropdownIconContainer,
  DropdownRowTitle,
  IconAlertCard,
  MobileTable,
  MobileTableRow,
  RowRightItems,
  TableCell,
  TableCellWalletBonus,
  TitleBonusCard,
} from './style/BonusAmountAvailable.style';

type DropdownBonusCardAmountAvailableProps = {
  onClose?: () => void;
};

export function DropdownBonusCardAmountAvailable({
  onClose,
}: DropdownBonusCardAmountAvailableProps) {
  const dispatch = useDispatch();
  const { getState } = useStore();

  const { hasAnyBonus } = useBonusData();

  const bonusSpendabilityLoadState = useSelector(
    selectBonusSpendabilityLoadState,
  );

  useEffect(() => {
    const apiConfig = selectGetBonusSpendabilityApiConfig(getState());
    dispatch(requestDataIfNecessary(apiConfig));
  }, [dispatch, getState]);

  const isDesktop = useIsAtLeastDesktopBreakpoint();

  return (
    <Card
      display="flex"
      flexDirection="column"
      gap="16px"
      padding={isDesktop ? '5px 10px 16px 10px' : '5px 11px 16px 11px'}
      border={0}
      borderRadius="8px"
      style={{ boxShadow: 'none' }}
    >
      <Flex
        justify="space-between"
        flexDirection="row"
        flexWrap="wrap"
        align="center"
        padding="5px"
        margin={isDesktop ? '5px 0 -20px 0' : '2px 0 -20px 0'}
        fluid
      >
        <div>
          <TitleBonusCard
            style={{
              fontSize: isDesktop ? 20 : 18,
              fontWeight: 800,
            }}
          >
            <Translate id="text.bonus.title" />
          </TitleBonusCard>
        </div>
        <InternalLink to="/bonus-promotions" onClick={onClose}>
          <Translate id="lbl.seeAll" />
        </InternalLink>
      </Flex>

      {!bonusSpendabilityLoadState.isLoaded &&
      !bonusSpendabilityLoadState.hasFailed ? (
        <LoaderWrapper>
          <Loader active viewMode="fullSize" />
        </LoaderWrapper>
      ) : !hasAnyBonus ? (
        <DropdownRowTitle style={{ marginTop: '24px' }}>
          <Translate id="lbl.bonus.noBonus" />
        </DropdownRowTitle>
      ) : (
        <BonusTable />
      )}
    </Card>
  );
}

export function BonusTable({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { getState, subscribe } = useStore();

  const {
    formatters: { formatCurrency },
  } = useI18n();

  const { realBonus, funBonus, freebet, freespin, freespinGold } =
    useBonusData();

  const spendability = useSelector(selectBonusSpendability);

  const [showSpendibilityTable, setShowSpendibilityTable] = useState(false);

  const hasRequestedRef = useRef(false);

  const isDesktop = useIsAtLeastDesktopBreakpoint();

  const handleToggleSpendibility = () => {
    setShowSpendibilityTable((prev) => {
      const next = !prev;
      if (!next) return next;

      if ((spendability?.length ?? 0) > 0) return next;

      if (hasRequestedRef.current) return prev;
      hasRequestedRef.current = true;

      const getSpendability = selectGetBonusSpendabilityApiConfig(getState());
      dispatch(requestDataIfNecessary(getSpendability));

      const unsubscribe = subscribe(() => {
        const { isLoaded, hasFailed } =
          selectBonusSpendabilityLoadState(getState());
        if (isLoaded) {
          setShowSpendibilityTable(true);
          unsubscribe();
        } else if (hasFailed) {
          setShowSpendibilityTable(false);
          unsubscribe();
        }
      });

      return prev;
    });
  };

  const realSpend =
    spendability?.find(
      (b) =>
        b.bonusTypeCode === 'BS' ||
        b.bonusTypeCode === '3' ||
        b.bonusTypeDescription?.toUpperCase().includes('STANDARD'),
    ) || null;

  const handleButtonClick = (bonusType: string) => {
    onClose?.();
    navigate({ to: '/bonus-promotions', search: { bonusType } });
  };

  return (
    <MobileTable>
      {realBonus != null && (
        <MobileTableRow style={{ marginTop: '8px' }}>
          <div style={{ width: '100%' }}>
            <Flex
              justify="space-between"
              align="center"
              flexDirection="row"
              fluid
              paddingRight={isDesktop ? '0px' : '5px'}
            >
              <DropdownRowTitle>
                <Translate id="lbl.bonus.selectReal" />
              </DropdownRowTitle>

              <Flex align="center">
                <DropdownBoldTableCell>
                  {formatCurrency(realBonus, { removeWhiteSpaces: true })}
                </DropdownBoldTableCell>
                <DropdownIconContainer onClick={() => handleButtonClick('BS')}>
                  <IconAlertCard
                    iconSrc={smallChevronRightSvg}
                    fill="black"
                    iconSize="14px"
                  />
                </DropdownIconContainer>
              </Flex>
            </Flex>

            {showSpendibilityTable && realSpend?.products?.length ? (
              <Flex padding={isDesktop ? '0' : '0px 5px'}>
                <TableSpecsDropdown style={{ marginTop: '16px' }}>
                  {realSpend.products.map((p, index, products) => {
                    const isLast = index === products.length - 1;
                    return (
                      <TableRowSpecs
                        key={p.code ?? index}
                        $insetDivider="8px"
                        style={{ padding: '8px 8px 0px 8px' }}
                      >
                        <TableCellWalletBonus>
                          <SpecsTitle $isLast={isLast}>
                            {p.description}
                          </SpecsTitle>
                        </TableCellWalletBonus>
                        <RowRightItems>
                          <SpecsParagraph $isLast={isLast}>
                            {formatCurrency(Number(p.residualAmount || 0), {
                              removeWhiteSpaces: true,
                            })}
                          </SpecsParagraph>
                        </RowRightItems>
                      </TableRowSpecs>
                    );
                  })}
                </TableSpecsDropdown>
              </Flex>
            ) : null}

            <Flex padding="16px 0 12px 0">
              <ShowSpendibilityDropdown onClick={handleToggleSpendibility}>
                <Translate
                  id={
                    showSpendibilityTable
                      ? 'text.bonusShowSpendabilityDropdownOff'
                      : 'text.bonusShowSpendabilityDropdown'
                  }
                />
              </ShowSpendibilityDropdown>
            </Flex>
          </div>
        </MobileTableRow>
      )}

      {freebet != null && (
        <MobileTableRow style={{ paddingTop: 0 }}>
          <TableCell>
            <DropdownRowTitle style={{ margin: '10px 0' }}>
              <Translate id="lbl.bonus.selectFreebet" />
            </DropdownRowTitle>
          </TableCell>
          <RowRightItems $isDropdown={true}>
            <DropdownBoldTableCell>
              {formatCurrency(freebet, { removeWhiteSpaces: true })}
            </DropdownBoldTableCell>
            <DropdownIconContainer
              style={{ paddingTop: 0 }}
              onClick={() => handleButtonClick('FB')}
            >
              <IconAlertCard
                iconSrc={smallChevronRightSvg}
                fill="black"
                iconSize="14px"
              />
            </DropdownIconContainer>
          </RowRightItems>
        </MobileTableRow>
      )}

      {funBonus != null && (
        <MobileTableRow style={{ paddingTop: 0 }}>
          <TableCell>
            <DropdownRowTitle style={{ margin: '10px 0' }}>
              <Translate id="lbl.bonus.selectFunbonus" />
            </DropdownRowTitle>
          </TableCell>
          <RowRightItems $isDropdown={true}>
            <DropdownBoldTableCell>
              {formatCurrency(funBonus, { removeWhiteSpaces: true })}
            </DropdownBoldTableCell>
            <DropdownIconContainer
              style={{ paddingTop: 0 }}
              onClick={() => handleButtonClick('FUN')}
            >
              <IconAlertCard
                iconSrc={smallChevronRightSvg}
                fill="black"
                iconSize="14px"
              />
            </DropdownIconContainer>
          </RowRightItems>
        </MobileTableRow>
      )}

      {freespin != null && (
        <MobileTableRow style={{ paddingTop: 0 }}>
          <TableCell>
            <DropdownRowTitle style={{ margin: '10px 0' }}>
              <Translate id="lbl.bonus.selectFreespin" />
            </DropdownRowTitle>
          </TableCell>
          <RowRightItems $isDropdown={true}>
            <DropdownBoldTableCell>
              {freespin}{' '}
              <Translate id="lbl.bonus.selectFreespin.walletAttribute" />
            </DropdownBoldTableCell>
            <DropdownIconContainer
              style={{ paddingTop: 0 }}
              onClick={() => handleButtonClick('FS')}
            >
              <IconAlertCard
                iconSrc={smallChevronRightSvg}
                fill="black"
                iconSize="14px"
              />
            </DropdownIconContainer>
          </RowRightItems>
        </MobileTableRow>
      )}

      {freespinGold != null && (
        <MobileTableRow style={{ paddingTop: 0 }}>
          <TableCell>
            <DropdownRowTitle style={{ margin: '10px 0' }}>
              <Translate id="lbl.bonus.selectFreespinGold" />
            </DropdownRowTitle>
          </TableCell>
          <RowRightItems $isDropdown={true}>
            <DropdownBoldTableCell>
              {freespinGold ? freespinGold : 0}
            </DropdownBoldTableCell>
            <DropdownIconContainer
              style={{ paddingTop: 0 }}
              onClick={() => handleButtonClick('FSG')}
            >
              <IconAlertCard
                iconSrc={smallChevronRightSvg}
                fill="black"
                iconSize="14px"
              />
            </DropdownIconContainer>
          </RowRightItems>
        </MobileTableRow>
      )}
    </MobileTable>
  );
}

const LoaderWrapper = styled.div`
  height: 100px;
`;
